import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Trash2, ShoppingCart, Users, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useCartStore, CartItem } from '@/store/cartStore';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatCurrency } from '@/lib/currencyFormatter';
import { cn } from '@/lib/currencyFormatter';
import { PublicMenuItemCard } from './public/PublicMenuItemCard';
import { MenuItem } from '@/types/menu';
import { supabase } from '@/integrations/supabase/client';

interface CartSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onPlaceOrder: () => void;
  storeId?: string;
}

export const CartSidePanel = React.memo<CartSidePanelProps>(({
  isOpen,
  onClose,
  onPlaceOrder,
  storeId,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [selectedTableId, setSelectedTableId] = useState<string>('');
  const [selectedTableNumber, setSelectedTableNumber] = useState<string>('');
  const [selectedTableName, setSelectedTableName] = useState<string>('');
  const [specialInstructions, setSpecialInstructions] = useState<string>('');
  const [tables, setTables] = useState<Array<{id: string, number: number, name: string}>>([]);
  const [isLoadingTables, setIsLoadingTables] = useState(false);

  const {
    items,
    selectedTableId: cartTableId,
    selectedTableNumber: cartTableNumber,
    selectedTableName: cartTableName,
    removeItem,
    updateQuantity,
    updateSpecialInstructions,
    getTotalItems,
    getTotalAmount,
    setTable,
    clearCart,
  } = useCartStore();

  const totalItems = getTotalItems();
  const totalAmount = getTotalAmount();

  // Fetch tables from database
  useEffect(() => {
    const fetchTables = async () => {
      if (!storeId) return;
      
      setIsLoadingTables(true);
      try {
        const { data, error } = await supabase
          .from('store_tables')
          .select('id, number, name')
          .eq('store_id', storeId)
          .order('number', { ascending: true });

        if (error) {
          console.error('Error fetching tables:', error);
          toast({
            title: t('waiterOrdering.errorLoadingTables'),
            description: t('waiterOrdering.errorLoadingTablesDescription'),
            variant: 'destructive',
          });
        } else {
          setTables(data || []);
        }
      } catch (error) {
        console.error('Error fetching tables:', error);
      } finally {
        setIsLoadingTables(false);
      }
    };

    fetchTables();
  }, [storeId, toast]);

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
      toast({
        title: t('waiterOrdering.itemRemoved'),
        description: t('waiterOrdering.itemRemovedDescription'),
      });
    } else {
      updateQuantity(itemId, newQuantity);
    }
  };

  const handlePlaceOrder = () => {
    if (!selectedTableId || !selectedTableNumber) {
      toast({
        title: t('waiterOrdering.tableSelectionRequired'),
        description: t('waiterOrdering.tableSelectionRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }

    if (totalItems === 0) {
      toast({
        title: t('waiterOrdering.emptyCart'),
        description: t('waiterOrdering.emptyCartDescription'),
        variant: 'destructive',
      });
      return;
    }

    // Set the selected table in cart store
    setTable(selectedTableId, selectedTableNumber, selectedTableName);
    
    // Call the parent's place order function
    onPlaceOrder();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={handleClose}
        />
      )}

      {/* Side Panel */}
      <div
        className={cn(
          'fixed z-50 bg-background border-l shadow-xl transition-all duration-300 ease-in-out flex flex-col',
          isMobile
            ? 'bottom-0 left-0 right-0 h-[85vh] rounded-t-lg border-t'
            : 'top-0 right-0 h-full w-96 border-l',
          isOpen
            ? isMobile
              ? 'translate-y-0'
              : 'translate-x-0'
            : isMobile
            ? 'translate-y-full'
            : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            <h2 className="text-lg font-semibold">{t('waiterOrdering.waiterCart')}</h2>
            {totalItems > 0 && (
              <Badge variant="secondary">{totalItems} items</Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                <ShoppingCart className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">{t('waiterOrdering.cartIsEmpty')}</p>
                <p className="text-sm">{t('waiterOrdering.cartIsEmptyDescription')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((cartItem) => (
                  <div
                    key={cartItem.item.id}
                    className="border rounded-lg p-4 space-y-3"
                  >
                    {/* Item Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium">{cartItem.item.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(Number(cartItem.item.price))}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(cartItem.item.id)}
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuantityChange(cartItem.item.id, cartItem.quantity - 1)}
                        className="h-8 w-8 p-0"
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center font-medium">
                        {cartItem.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuantityChange(cartItem.item.id, cartItem.quantity + 1)}
                        className="h-8 w-8 p-0"
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Special Instructions */}
                    <div>
                      <label className="text-sm font-medium">{t('waiterOrdering.specialInstructions')}</label>
                      <Textarea
                        placeholder={t('waiterOrdering.specialInstructionsPlaceholder')}
                        value={cartItem.specialInstructions || ''}
                        onChange={(e) => updateSpecialInstructions(cartItem.item.id, e.target.value)}
                        className="mt-1 text-sm"
                        rows={2}
                      />
                    </div>

                    {/* Item Total */}
                    <div className="text-right">
                      <span className="font-medium">
                        {formatCurrency(Number(cartItem.item.price) * cartItem.quantity)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t p-4 space-y-4 bg-background sticky bottom-0">
              {/* Table Selection */}
              <div>
                <label className="text-sm font-medium flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4" />
                  {t('waiterOrdering.selectTableRequired')}
                </label>
                <select
                  value={selectedTableId}
                  onChange={(e) => {
                    const table = tables.find(t => t.id === e.target.value);
                    setSelectedTableId(e.target.value);
                    if (table) {
                      setSelectedTableNumber(table.number.toString());
                      setSelectedTableName(table.name);
                    }
                  }}
                  className="w-full p-2 border rounded-md bg-background text-foreground"
                  disabled={isLoadingTables}
                >
                  <option value="" className="bg-background text-foreground">
                    {isLoadingTables ? t('waiterOrdering.loadingTables') : t('waiterOrdering.chooseTable')}
                  </option>
                  {tables.map((table) => (
                    <option key={table.id} value={table.id} className="bg-background text-foreground">
                      {table.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Order Instructions */}
              <div>
                <label className="text-sm font-medium mb-2">{t('waiterOrdering.orderInstructions')}</label>
                <Textarea
                  placeholder={t('waiterOrdering.orderInstructionsPlaceholder')}
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  className="text-sm"
                  rows={2}
                />
              </div>

              {/* Total */}
              <div className="flex items-center justify-between text-lg font-semibold">
                <span>{t('waiterOrdering.total')}</span>
                <span>{formatCurrency(totalAmount)}</span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={clearCart}
                  className="flex-1 h-10"
                >
                  {t('waiterOrdering.clearCart')}
                </Button>
                <Button
                  onClick={handlePlaceOrder}
                  disabled={!selectedTableId || totalItems === 0}
                  className={cn(
                    "flex-1 h-10",
                    !selectedTableId 
                      ? "bg-gray-400 cursor-not-allowed" 
                      : "bg-primary hover:bg-primary/90"
                  )}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {!selectedTableId ? t('waiterOrdering.selectTableFirst') : t('waiterOrdering.placeOrder')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
});

CartSidePanel.displayName = 'CartSidePanel'; 